import {InfoIcon, PencilIcon} from '@primer/octicons-react'
import {Link, ProgressBar, Tooltip} from '@primer/react'

import {formatQuotaResetLabel} from '../hooks/use-quota-banner'
import {MANAGE_BUDGET_URL} from '../utils/copilot-chat-helpers'
import {CopilotPlan} from '../utils/copilot-chat-types'
import {useEntitlement} from './quota/EntitlementContext'
import styles from './TokenUsageInfo.module.css'

interface TokenUsageInfoData {
  includedCredits: {
    percentUsed: number
    isExceeded: boolean
    resetLabel: string | null
    total: number | null
    used: number | null
  }
  additionalUsage: {
    enabled: boolean
    percentUsed: number
    isExceeded: boolean
    budgetAmount: number | null
    budgetSpend: number | null
  } | null
}

const numberFormatter = new Intl.NumberFormat('en-US')
const currencyFormatter = new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD'})

function formatNumber(amount: number | null): string {
  if (amount === null) return '-'
  return numberFormatter.format(amount)
}

function formatCurrency(amount: number | null): string {
  if (amount === null) return '-'
  return currencyFormatter.format(amount)
}

function useTokenUsageInfo(): TokenUsageInfoData | null {
  const {
    plan,
    overagesRemaining,
    overagesExceeded,
    overagesEnabled,
    overagesBudgetAmount,
    overagesBudgetSpend,
    quotaExceeded,
    quotaRemaining,
    quotaTotalAmount,
    quotaUsedAmount,
    quotaResetAt,
    licenseProvisioning,
    isCommercialPlan,
  } = useEntitlement()

  // Don't show while the license is still being provisioned to avoid misleading states.
  if (licenseProvisioning) return null

  // No quota data available yet.
  if (quotaRemaining === undefined) return null

  const percentUsed = Math.max(0, Math.min(100, 100 - quotaRemaining))
  const resetLabel = formatQuotaResetLabel(quotaResetAt)

  const isIndividualPaid =
    plan === CopilotPlan.IndividualPro ||
    plan === CopilotPlan.IndividualProPlus ||
    plan === CopilotPlan.IndividualMax ||
    plan === CopilotPlan.IndividualStudent
  const showAdditionalUsage = isIndividualPaid
  const overagePercentUsed = overagesRemaining !== undefined ? Math.max(0, Math.min(100, 100 - overagesRemaining)) : 0

  return {
    includedCredits: {
      percentUsed,
      isExceeded: quotaExceeded,
      resetLabel: isCommercialPlan ? null : resetLabel,
      total: quotaTotalAmount ?? null,
      used: quotaUsedAmount ?? null,
    },
    additionalUsage: showAdditionalUsage
      ? {
          enabled: overagesEnabled,
          percentUsed: overagePercentUsed,
          isExceeded: overagesExceeded,
          budgetAmount: overagesBudgetAmount ?? null,
          budgetSpend: overagesBudgetSpend ?? null,
        }
      : null,
  }
}

export function TokenUsageInfo() {
  const data = useTokenUsageInfo()

  if (!data) return null

  const {includedCredits, additionalUsage} = data

  return (
    <div>
      <div className={styles.tokenUsageSection}>
        <div className={styles.tokenUsageSectionHeader}>
          <span className={styles.tokenUsageSectionTitle}>
            Included credits
            <Tooltip text="AI credits bundled with your Copilot plan. Resets each billing cycle." direction="e">
              <button className={styles.tokenUsageInfoButton} aria-label="About included credits" type="button">
                <InfoIcon size={16} className={styles.tokenUsageInfoIcon} />
              </button>
            </Tooltip>
          </span>
        </div>
        <div className={styles.tokenUsageDetails}>
          <span className={styles.tokenUsageDetailMuted}>
            {includedCredits.resetLabel ? `Reset ${includedCredits.resetLabel}` : ''}
          </span>
          {includedCredits.total != null && includedCredits.used != null ? (
            <span className={styles.tokenUsageDetail}>
              {formatNumber(includedCredits.used)}
              <span className={styles.tokenUsageDetailMuted}> / {formatNumber(includedCredits.total)} AI credits</span>
            </span>
          ) : null}
        </div>
        <ProgressBar barSize="small">
          <ProgressBar.Item
            progress={includedCredits.percentUsed}
            bg={includedCredits.percentUsed >= 75 ? 'severe.emphasis' : 'accent.emphasis'}
            aria-label={`Included credits: ${includedCredits.percentUsed}% used`}
            data-progress-bar-bg={includedCredits.percentUsed >= 75 ? 'severe.emphasis' : 'accent.emphasis'}
          />
        </ProgressBar>
      </div>

      {additionalUsage && (
        <>
          <div className={styles.tokenUsageDivider} />
          <div className={styles.tokenUsageSection}>
            <div className={styles.tokenUsageSectionHeader}>
              <span className={styles.tokenUsageSectionTitle}>
                Additional usage
                <Tooltip
                  text="Usage beyond your included credits, billed separately at pay-as-you-go rates."
                  direction="e"
                >
                  <button className={styles.tokenUsageInfoButton} aria-label="About additional usage" type="button">
                    <InfoIcon size={16} className={styles.tokenUsageInfoIcon} />
                  </button>
                </Tooltip>
              </span>
              <Link href={MANAGE_BUDGET_URL} className={styles.editBudgetLink}>
                <PencilIcon size={12} />
                Edit budget
              </Link>
            </div>
            <div className={styles.tokenUsageDetails}>
              <span className={styles.tokenUsageDetailMuted}>
                {additionalUsage.isExceeded ? 'Exceeded' : additionalUsage.enabled ? 'Enabled' : 'Disabled'}
              </span>
              {additionalUsage.budgetAmount != null && additionalUsage.budgetSpend != null ? (
                <span
                  className={styles.tokenUsageDetail}
                >{`${formatCurrency(additionalUsage.budgetSpend)} / ${formatCurrency(additionalUsage.budgetAmount)}`}</span>
              ) : null}
            </div>
            <ProgressBar
              progress={additionalUsage.percentUsed}
              bg={additionalUsage.percentUsed >= 75 ? 'severe.emphasis' : 'accent.emphasis'}
              barSize="small"
              aria-label={`Additional usage: ${additionalUsage.percentUsed}% used`}
            />
          </div>
        </>
      )}
    </div>
  )
}
